import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Smartphone, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const TwoFactorSetup = ({ onComplete, onBack, shouldCompleteOnboarding = true }) => {
  const [step, setStep] = useState('setup'); // 'setup', 'verification', 'success'
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    initializeSetup();
  }, []);

  const initializeSetup = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/2fa/setup');
      setSetupData(response.data);
      setStep('setup');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to initialize 2FA setup');
      toast.error('Failed to initialize 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await axios.post('/api/2fa/verify-setup', {
        code: verificationCode.trim()
      });
      
      // Only mark onboarding as complete if this component is supposed to complete onboarding
      // (i.e., when accessed from profile, not from onboarding flow)
      if (shouldCompleteOnboarding) {
        try {
          await axios.post('/api/users/complete-onboarding', {}, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (onboardingError) {
          console.error('Error completing onboarding:', onboardingError);
          // Continue even if onboarding completion fails
        }
      }
      
      setStep('success');
      toast.success('2FA enabled successfully!');
      
      // Wait a moment then call onComplete
      setTimeout(() => {
        onComplete();
      }, 2000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid verification code');
      toast.error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'verification') {
      setStep('setup');
      setVerificationCode('');
      setError('');
    } else if (onBack) {
      onBack();
    }
  };

  if (loading && !setupData) {
    return (
      <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing 2FA setup...</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2FA Enabled Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Your account is now protected with two-factor authentication.
          </p>
          <div className="animate-pulse">
            <p className="text-sm text-gray-500">Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Setup Two-Factor Authentication</h1>
            <div className="w-9"></div> {/* Spacer for centering */}
          </div>
        </div>

        <div className="p-6">
          {step === 'setup' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Secure Your Account</h2>
                <p className="text-gray-600">
                  Two-factor authentication adds an extra layer of security to your account.
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Step 1: Scan QR Code</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code:
                  </p>
                  
                  {setupData?.qr_code && (
                    <div className="text-center">
                      <img
                        src={setupData.qr_code}
                        alt="2FA QR Code"
                        className="mx-auto border border-gray-200 rounded-lg"
                        style={{ width: '200px', height: '200px' }}
                      />
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Step 2: Manual Entry (Alternative)</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    If you can't scan the QR code, manually enter this code in your authenticator app:
                  </p>
                  <div className="bg-white border border-gray-300 rounded-lg p-3">
                    <code className="text-sm font-mono text-gray-800 break-all">
                      {setupData?.secret}
                    </code>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">Recommended Apps</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Google Authenticator</li>
                        <li>• Authy</li>
                        <li>• Microsoft Authenticator</li>
                        <li>• 1Password</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setStep('verification')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Continue to Verification
              </button>
            </div>
          )}

          {step === 'verification' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Verify Setup</h2>
                <p className="text-gray-600">
                  Enter the 6-digit code from your authenticator app to complete the setup.
                </p>
              </div>

              <form onSubmit={handleVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep('setup')}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !verificationCode.trim()}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
